/*
 * Страница RustFS кластера (Component rustfs-<кластер>): подключение к S3 и бакеты платформы,
 * ссылки (консоль, ArgoCD, Grafana), приложение ArgoCD, поды namespace rustfs и метрики
 * (доступность, поды, ресурсы, место на узлах; OTLP-метрики самого RustFS).
 */
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';
import {
  EntityAboutCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
} from '@backstage/plugin-catalog';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';
import {
  EntityArgoCDOverviewCard,
  isArgocdAvailable,
} from '@roadiehq/backstage-plugin-argo-cd';
import {
  DATASTORE_CLUSTER_ANNOTATION,
  RUSTFS_INTERNAL_S3,
  RUSTFS_POOL_ANNOTATION,
  RUSTFS_REPLICAS_ANNOTATION,
  RUSTFS_S3_URL_ANNOTATION,
  annotation,
  rustfsBuckets,
} from '../../lib/viva';
import { rustfsMode } from '../catalog/columns';
import { MetricsContent, metricsClusterOf } from '../metrics/MetricsContent';

const Row = ({ label, value }: { label: string; value: string }) => (
  <TableRow>
    <TableCell component="th" scope="row">
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
    </TableCell>
    <TableCell>
      <Typography variant="body2">{value}</Typography>
    </TableCell>
  </TableRow>
);

/** Как подключиться к S3: адреса внутри и снаружи кластера, регион, ключи. */
export const RustfsConnectionCard = () => {
  const { entity } = useEntity();
  return (
    <InfoCard title="Подключение к S3">
      <Table size="small">
        <TableBody>
          <Row
            label="Режим"
            value={rustfsMode(annotation(entity, RUSTFS_REPLICAS_ANNOTATION))}
          />
          <Row
            label="Пул узлов"
            value={annotation(entity, RUSTFS_POOL_ANNOTATION) || 'общие узлы'}
          />
          <Row label="Внутри кластера" value={RUSTFS_INTERNAL_S3} />
          <Row
            label="Снаружи"
            value={annotation(entity, RUSTFS_S3_URL_ANNOTATION) ?? '—'}
          />
          <Row label="Регион, адресация" value="us-east-1, path-style" />
          <Row
            label="Ключи"
            value="root — секрет rustfs/rustfs-root (сервисы платформы); только чтение датасетов и MLflow — rustfs/rustfs-notebooks (ноутбуки JupyterHub)"
          />
        </TableBody>
      </Table>
    </InfoCard>
  );
};

/** Бакеты, которые заводит платформа (PostSync-хук rustfs-bootstrap), и кто ими пользуется. */
export const RustfsBucketsCard = () => {
  const { entity } = useEntity();
  const cluster = annotation(entity, DATASTORE_CLUSTER_ANNOTATION) ?? '';
  return (
    <InfoCard title="Бакеты платформы">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Бакет</TableCell>
            <TableCell>Кто пользуется</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rustfsBuckets(cluster).map(b => (
            <TableRow key={b.bucket}>
              <TableCell>
                <Typography variant="body2">{b.bucket}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{b.usedBy}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </InfoCard>
  );
};

export const rustfsEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6} xs={12}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <RustfsConnectionCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <RustfsBucketsCard />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={e => Boolean(isArgocdAvailable(e))}>
            <Grid item md={12} xs={12}>
              <EntityArgoCDOverviewCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/metrics"
      title="Метрики"
      if={e => Boolean(metricsClusterOf(e))}
    >
      <MetricsContent />
    </EntityLayout.Route>
  </EntityLayout>
);
